import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Divider,
  Pagination,
  MenuItem,
  Menu,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Layout from '../../components/layout/Layout';
import purchaseOrderService from '../../services/purchaseOrderService';
import axiosInstance from '../../utils/axios';
import { formatDate, formatTime } from '../../utils/dateFormatter';

const formatOrderTime = (createdAt) => {
  if (!createdAt) return '';
  return formatTime(createdAt);
};

const formatCreatedDate = (createdAt) => {
  if (!createdAt) return '';
  return formatDate(createdAt);
};

const ViewPurchaseOrdersPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10
  });
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  
  // Menu state
  const isMenuOpen = Boolean(menuAnchorEl);

  // Fetch orders with pagination
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Call the API to get all purchase orders with pagination
      const result = await purchaseOrderService.getAllPurchaseOrders({
        page: pagination.page,
        size: pagination.size,
        sort: 'orderDate',
        direction: 'DESC'
      });
      
      // Process the data and enrich with supplier info if needed
      const ordersData = result.content || [];
      
      // Filter out orders with RETURNED status or orderNumber starting with RETURN-
      const filteredOrdersData = ordersData.filter(order => 
        order.status !== 'RETURNED' && 
        !(order.orderNumber && order.orderNumber.startsWith('RETURN-'))
      );
      
      console.log(`Filtered out ${ordersData.length - filteredOrdersData.length} returned orders`);
      
      // For each order, fetch the supplier data if the order doesn't have supplier name
      const enrichedOrders = await Promise.all(filteredOrdersData.map(async (order) => {
        // Create a new order object with properly mapped fields
        const enhancedOrder = {
          ...order,
          // Use orderNumber if available, otherwise fallback to id
          displayId: order.orderNumber || `#${order.id}`,
        };
        
        // Format date properly - handle various date formats
        if (order.orderDate) {
          try {
            // If it's in a numeric format like 202547, convert to proper date
            if (/^\d{5,6}$/.test(order.orderDate)) {
              // Attempt to parse as YYYYDDD format (year and day of year)
              const year = order.orderDate.substring(0, 4);
              const dayOfYear = parseInt(order.orderDate.substring(4));
              
              const date = new Date(parseInt(year), 0); // Start with Jan 1
              date.setDate(dayOfYear); // Add days
              
              enhancedOrder.orderDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
            } 
            // For other formats, try to format nicely
            else {
              const parsedDate = new Date(order.orderDate);
              if (!isNaN(parsedDate.getTime())) {
                enhancedOrder.orderDate = `${parsedDate.getMonth() + 1}/${parsedDate.getDate()}/${parsedDate.getFullYear()}`;
              }
            }
          } catch (error) {
            console.error('Error parsing date:', error);
          }
        }
        
        // Format delivery date if available
        if (order.deliveryDate) {
          try {
            // If it's in a numeric format like 202547, convert to proper date
            if (/^\d{5,6}$/.test(order.deliveryDate)) {
              // Attempt to parse as YYYYDDD format (year and day of year)
              const year = order.deliveryDate.substring(0, 4);
              const dayOfYear = parseInt(order.deliveryDate.substring(4));
              
              const date = new Date(parseInt(year), 0); // Start with Jan 1
              date.setDate(dayOfYear); // Add days
              
              enhancedOrder.deliveryDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
            } 
            // For other formats, try to format nicely
            else {
              const parsedDate = new Date(order.deliveryDate);
              if (!isNaN(parsedDate.getTime())) {
                enhancedOrder.deliveryDate = `${parsedDate.getMonth() + 1}/${parsedDate.getDate()}/${parsedDate.getFullYear()}`;
              }
            }
          } catch (error) {
            console.error('Error parsing delivery date:', error);
          }
        }
        
        // Handle supplier data with higher priority on supplier object
        if (order.supplier) {
          if (typeof order.supplier === 'object') {
            // Extract from supplier object - check for name field first
            if (order.supplier.name) {
              enhancedOrder.supplierName = order.supplier.name;
            } else {
              enhancedOrder.supplierName = 'Unknown Supplier';
            }
          } else if (typeof order.supplier === 'string' && order.supplier.trim()) {
            // Use supplier directly if it's a string
            enhancedOrder.supplierName = order.supplier;
          }
        } 
        
        // Fallback to supplier lookup by ID if needed
        if (!enhancedOrder.supplierName && order.supplierId) {
          try {
            const supplierData = await purchaseOrderService.getSupplierById(order.supplierId);
            if (supplierData) {
              enhancedOrder.supplierName = supplierData.name || 'Unknown Supplier';
            } else {
              enhancedOrder.supplierName = 'Unknown Supplier';
            }
          } catch (error) {
            console.error(`Error fetching supplier for order ${order.id}:`, error);
            enhancedOrder.supplierName = 'Unknown Supplier';
          }
        } else if (!enhancedOrder.supplierName) {
          enhancedOrder.supplierName = 'Unknown Supplier';
        }
        
        return enhancedOrder;
      }));
      
      setOrders(enrichedOrders);
      setFilteredOrders(enrichedOrders);
      
      setPagination({
        ...pagination,
        totalPages: result.totalPages || 1,
        totalElements: result.totalElements || ordersData.length
      });
      
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      enqueueSnackbar('Failed to load purchase orders: ' + (error.message || 'Unknown error'), { variant: 'error' });
      
      // Fallback to mock data for development/testing purposes
      console.warn('Falling back to mock data due to API error');
      const mockOrders = [
        {
          id: 2001,
          supplierId: 1,
          supplierName: "Acme Supplies",
          orderDate: "2023-06-15",
          status: "RECEIVED",
          paymentStatus: "PAID",
          paymentMethod: "BANK_TRANSFER",
          subtotal: 1256.99,
          taxAmount: 100.56,
          discount: 50.00,
          total: 1307.55,
          items: [
            { productId: 1, productName: "Printer Paper", quantity: 50, unitPrice: 5.99, total: 299.50 },
            { productId: 2, productName: "Toner Cartridge", quantity: 10, unitPrice: 45.00, total: 450.00 }
          ]
        },
        {
          id: 2002,
          supplierId: 2,
          supplierName: "Global Distribution",
          orderDate: "2023-06-16",
          status: "PENDING",
          paymentStatus: "PENDING",
          paymentMethod: "CREDIT_CARD",
          subtotal: 3489.97,
          taxAmount: 279.20,
          discount: 100.00,
          total: 3669.17,
          items: [
            { productId: 3, productName: "Desktop Computer", quantity: 3, unitPrice: 899.99, total: 2699.97 },
            { productId: 4, productName: "Monitor", quantity: 5, unitPrice: 199.00, total: 995.00 }
          ]
        },
        {
          id: 2003,
          supplierId: 3,
          supplierName: "Tech Wholesale",
          orderDate: "2023-06-17",
          status: "CANCELLED",
          paymentStatus: "CANCELLED",
          paymentMethod: "BANK_TRANSFER",
          subtotal: 549.99,
          taxAmount: 44.00,
          discount: 0.00,
          total: 593.99,
          items: [
            { productId: 5, productName: "Wireless Router", quantity: 5, unitPrice: 59.99, total: 299.95 },
            { productId: 6, productName: "External Hard Drive", quantity: 2, unitPrice: 125.00, total: 250.00 }
          ]
        }
      ];
      
      // Filter the mock data to exclude any orders with RETURNED status for consistency
      const filteredMockOrders = mockOrders.filter(order => 
        order.status !== 'RETURNED' && 
        !(order.orderNumber && order.orderNumber.startsWith('RETURN-'))
      );
      
      setOrders(filteredMockOrders);
      setFilteredOrders(filteredMockOrders);
      
      setPagination({
        ...pagination,
        totalPages: 1,
        totalElements: filteredMockOrders.length
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, enqueueSnackbar]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Filter orders based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredOrders(orders);
      return;
    }
    
    // Note: orders array already excludes RETURNED orders and those with orderNumber starting with "RETURN-"
    const lowerQuery = searchQuery.toLowerCase();
    const filtered = orders.filter(order => 
      (order.displayId && order.displayId.toLowerCase().includes(lowerQuery)) ||
      (order.invoiceNumber && order.invoiceNumber.toLowerCase().includes(lowerQuery)) ||
      (order.supplierName && order.supplierName.toLowerCase().includes(lowerQuery)) ||
      (order.status && order.status.toLowerCase().includes(lowerQuery)) ||
      (order.deliveryDate && order.deliveryDate.toLowerCase().includes(lowerQuery)) ||
      (order.paymentStatus && order.paymentStatus.toLowerCase().includes(lowerQuery)) ||
      (order.total && order.total.toString().includes(lowerQuery))
    );
    
    setFilteredOrders(filtered);
  }, [searchQuery, orders]);

  // Handle search input change
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // View order details
  const handleViewOrder = async (order) => {
    try {
      setSelectedOrder(order);
      
      // If order details are not loaded yet, fetch them
      if (!order.items || order.items.length === 0) {
        console.log("Fetching order details for order ID:", order.id);
        const orderDetails = await purchaseOrderService.getPurchaseOrderById(order.id);
        if (orderDetails) {
          console.log("Received order details:", orderDetails);
          console.log("Order items:", orderDetails.items);
          
          // Merge the fetched details with the existing order data
          setSelectedOrder({
            ...order,
            ...orderDetails,
            // Ensure we keep the enhanced fields like displayId and supplierName
            displayId: order.displayId,
            supplierName: order.supplierName || orderDetails.supplierName
          });
        }
      }
      
      setOrderDetailOpen(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      enqueueSnackbar('Failed to load order details: ' + (error.message || 'Unknown error'), { variant: 'error' });
    }
  };

  // Menu handlers
  const handleMenuOpen = (event, orderId) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedOrderId(orderId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedOrderId(null);
  };

  // Pagination handler
  const handlePageChange = (event, value) => {
    setPagination({
      ...pagination,
      page: value - 1 // MUI Pagination is 1-indexed, our API is 0-indexed
    });
  };

  // Status color mapping
  const getStatusColor = (status) => {
    const statusMap = {
      'RECEIVED': 'success',
      'RECEIVED_COMPLETE': 'success',
      'PENDING': 'warning',
      'CANCELLED': 'error',
      'PARTIALLY_RECEIVED': 'info',
      'ORDERED': 'secondary'
    };
    
    return statusMap[status] || 'default';
  };

  // Payment status color mapping
  const getPaymentStatusColor = (status) => {
    const statusMap = {
      'PAID': 'success',
      'PENDING': 'warning',
      'CANCELLED': 'error',
      'PARTIALLY_PAID': 'info',
      'UNPAID': 'error'
    };
    
    return statusMap[status] || 'default';
  };

  // Format payment method for display
  const formatPaymentMethod = (method) => {
    if (!method) return 'N/A';
    
    // Convert SNAKE_CASE to Title Case
    return method
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Print receipt handler
  const handlePrintReceipt = async (orderId) => {
    try {
      handleMenuClose();
      // Logic to print receipt will be implemented here
      enqueueSnackbar('Printing receipt...', { variant: 'info' });
    } catch (error) {
      console.error('Error printing receipt:', error);
      enqueueSnackbar('Failed to print receipt: ' + (error.message || 'Unknown error'), { variant: 'error' });
    }
  };

  // Download invoice handler
  const handleDownloadInvoice = async (orderId) => {
    try {
      handleMenuClose();
      console.log('Attempting to download purchase order invoice for ID:', orderId);
      
      const response = await axiosInstance.get(`/purchase-orders/${orderId}/receipt`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      console.log('Response received:', response);
      console.log('Response data type:', typeof response.data);
      console.log('Response data size:', response.data?.size || 'unknown');

      // Create a blob from the PDF stream
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create a link element
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `purchase-order-${orderId}.pdf`;
      
      // Append to body, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      window.URL.revokeObjectURL(link.href);
      
      enqueueSnackbar('Invoice downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error downloading invoice:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      if (error.response?.status === 404) {
        enqueueSnackbar('Invoice not found', { variant: 'error' });
      } else if (error.response?.status === 403) {
        enqueueSnackbar('You do not have permission to download this invoice', { variant: 'error' });
      } else if (error.response?.status === 500) {
        enqueueSnackbar('Server error occurred while generating invoice', { variant: 'error' });
      } else {
        enqueueSnackbar(`Failed to download invoice: ${error.message || 'Unknown error'}`, { variant: 'error' });
      }
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                Purchase Orders
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate('/purchases/po-keyboard')}
              >
                Create New Order
              </Button>
            </Box>
            
            <Paper sx={{ p: 2, mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search by order ID, supplier, status, delivery date, or amount"
                variant="outlined"
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Paper>
            
            {loading && <LinearProgress sx={{ mb: 2 }} />}
            
            {/* Orders table */}
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Invoice No.</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Created Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.length > 0 ? (
                    filteredOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>{order.displayId}</TableCell>
                        <TableCell>{order.invoiceNumber || '-'}</TableCell>
                        <TableCell>{order.orderDate}</TableCell>
                        <TableCell>{formatOrderTime(order.createdAt)}</TableCell>
                        <TableCell>{formatCreatedDate(order.createdAt)}</TableCell>
                        <TableCell>{order.supplierName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={order.status} 
                            color={getStatusColor(order.status)}
                            size="small"
                            sx={{ 
                              textTransform: 'uppercase',
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {order.total ? `₹${Number(order.total).toFixed(2)}` : 'N/A'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleViewOrder(order)}
                            sx={{ mx: 0.5 }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, order.id)}
                            sx={{ mx: 0.5 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body1" sx={{ py: 2 }}>
                          {loading ? 'Loading orders...' : 'No purchase orders found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination 
                  count={pagination.totalPages} 
                  page={pagination.page + 1} 
                  onChange={handlePageChange} 
                  color="primary" 
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      
      {/* Order Details Dialog */}
      <Dialog 
        open={orderDetailOpen} 
        onClose={() => setOrderDetailOpen(false)}
        fullWidth
        maxWidth="md"
      >
        {selectedOrder && (
          <>
            <DialogTitle sx={{ pb: 0 }}>
              Purchase Order {selectedOrder.displayId}
            </DialogTitle>
            <Box sx={{ px: 3, pb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedOrder.orderDate}
              </Typography>
            </Box>
            <DialogContent dividers>
              <Grid container spacing={3}>
                {/* Order Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6">Order Information</Typography>
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Chip 
                          label={selectedOrder.status} 
                          color={getStatusColor(selectedOrder.status)}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Order Date</Typography>
                        <Typography variant="body1">
                          {selectedOrder.orderDate || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Invoice Number</Typography>
                        <Typography variant="body1">
                          {selectedOrder.invoiceNumber || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Delivery Date</Typography>
                        <Typography variant="body1">
                          {selectedOrder.deliveryDate || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Supplier Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6">Supplier Information</Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {selectedOrder.supplierName || 'Unknown Supplier'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {selectedOrder.supplier && selectedOrder.supplier.email 
                        ? selectedOrder.supplier.email 
                        : 'N/A'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">
                      {selectedOrder.supplier && selectedOrder.supplier.phone 
                        ? selectedOrder.supplier.phone 
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                {/* Order Items */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Order Items</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrder.items && selectedOrder.items.length > 0 ? (
                          selectedOrder.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {item.product && item.product.name ? 
                                  item.product.name : 
                                  (item.productName || `Product ID: ${item.productId || 'Unknown'}`)}
                              </TableCell>
                              <TableCell align="right">₹{(item.unitPrice || 0).toFixed(2)}</TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right">₹{(item.total || (item.quantity * item.unitPrice) || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">No items found</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Order Summary */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 2 }}>
                    <Typography variant="h6" sx={{ alignSelf: 'flex-start', mb: 1 }}>Order Calculation</Typography>
                    
                    {/* Subtotal */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                      <Typography variant="body1">₹{(selectedOrder.subtotal || 0).toFixed(2)}</Typography>
                    </Box>
                    
                    {/* Tax amount */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Tax Amount:
                      </Typography>
                      <Typography variant="body1">₹{(selectedOrder.taxAmount || 0).toFixed(2)}</Typography>
                    </Box>
                    
                    {/* Discount */}
                    {(selectedOrder.discount && selectedOrder.discount > 0) && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Discount:</Typography>
                        <Typography variant="body1" color="error">
                          -₹{(selectedOrder.discount || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Shipping cost if applicable */}
                    {(selectedOrder.shippingCost && selectedOrder.shippingCost > 0) && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Shipping Cost:</Typography>
                        <Typography variant="body1">₹{selectedOrder.shippingCost.toFixed(2)}</Typography>
                      </Box>
                    )}
                    
                    <Divider sx={{ width: '350px', my: 1 }} />
                    
                    {/* Total */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '350px' }}>
                      <Typography variant="h6">Total:</Typography>
                      <Typography variant="h6" color="primary.main">
                        ₹{(selectedOrder.total || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                {/* Notes Section */}
                {selectedOrder.notes && (
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="h6">Notes</Typography>
                    <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                      <Typography variant="body2">{selectedOrder.notes}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOrderDetailOpen(false)}>Close</Button>
              <Button 
                variant="contained" 
                startIcon={<DownloadIcon />}
                color="primary"
                onClick={() => handleDownloadInvoice(selectedOrder.id)}
              >
                Download Invoice
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Actions Menu */}
      <Menu
        id="order-menu"
        anchorEl={menuAnchorEl}
        keepMounted
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const order = orders.find(o => o.id === selectedOrderId);
          handleMenuClose();
          handleViewOrder(order);
        }}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          const order = orders.find(o => o.id === selectedOrderId);
          handleMenuClose();
          if (order) handleDownloadInvoice(order.id);
        }}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download Invoice
        </MenuItem>
      </Menu>
    </Layout>
  );
};

export default ViewPurchaseOrdersPage; 